// screens/ThreadSpread.tsx — B16c: one thread, whole. The B16a read-only
// spread grew its three working parts, on the same route:
//
//   SUMMARY ON OPEN — B15's async door, polled while 'composing'. A machine
//   reading is never dressed as fact: the paragraph wears the interpreted
//   label with the model named, and a citation chip says exactly what it
//   read. 'not_needed' renders NOTHING (a short thread is its own summary —
//   no chrome for an absent thing); 'failed' is one quiet line, because the
//   thread below is complete either way. The composing→ready settle is the
//   one blessed motion on this screen: it answers "where did this paragraph
//   come from".
//
//   ATTRIBUTION — the current filing as a chip (product · source word ·
//   confidence when AI), tap-to-correct through the HUMAN door (the server
//   hardcodes source='human'). One action, corrected, recorded.
//
//   REPLY — text-first composer. Two exits, one line between them:
//     Send        -> POST /api/threads/:id/reply (CODED; a typed reply IS
//                    the approval — no model, no proposal row)
//     Draft w/ AI -> POST /api/threads/:id/draft-reply; the draft lands IN
//                    the composer, editable, wearing its banner (model
//                    named, rationale shown). Send draft = the approve door
//                    with the edited text as overrides; Discard = reject,
//                    and the composer CLEARS — discarded model words are
//                    discarded, never laundered into a "typed" reply.
//   The receipt is the thread itself: on success the outbound message the
//   SERVER recorded appears in place (send-first-record-second is its law;
//   this screen renders what the server says happened, never what it hopes).
//   With no outbound mail path (Eden proper before B16e) the composer states
//   that truth once and disables — 503 is a fact, not an error toast.
import { useEffect, useRef, useState } from 'preact/hooks';
import { InterpretationLabel, SourceCitationChip } from '@vowric/ui';
import {
  ApiError,
  approveEmailDraft,
  draftThreadReply,
  getThread,
  getThreadAttribution,
  getThreadSummary,
  listProducts,
  putThreadAttribution,
  rejectProposal,
  sendThreadReply,
  setThreadState,
  type ProductApi,
  type ThreadAttributionApi,
  type WhoIsThisApi,
  type ThreadMessageApi,
  type ThreadRowApi,
  type ThreadSummaryApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { threadStateLabel } from './Threads';
import { WhoIsThisPanel } from '../components/WhoIsThisPanel';
import './ThreadSpread.css';

export interface ThreadSpreadProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  /** What the way back is CALLED — the origin's own name (Today, On me…).
   * Both Back and Mark done land there: done must visibly disappear the
   * thread, which the all view (closed included) cannot do. */
  backLabel?: string;
  /** Where "Mark done" lands you — defaults to onBack's destination. */
  onDone?: () => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; thread: ThreadRowApi; messages: ThreadMessageApi[] };

type SummaryState =
  | { phase: 'idle' }
  | { phase: 'composing' }
  | { phase: 'ready'; summary: ThreadSummaryApi }
  | { phase: 'failed' }
  | { phase: 'none' };

type ComposerMode =
  | { kind: 'typed' }
  | { kind: 'drafting' } // the model is writing
  | { kind: 'draft'; proposalId: string; rationale: string; subject: string }
  | { kind: 'sending' };

const SUMMARY_POLL_MS = 3_000;
const SUMMARY_POLL_MAX = 40; // 2 minutes of patience, then the quiet line

function messageTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function ThreadSpread({ tenantId, id, onBack, backLabel, onDone }: ThreadSpreadProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [summary, setSummary] = useState<SummaryState>({ phase: 'idle' });
  const [attribution, setAttribution] = useState<ThreadAttributionApi | null>(null);
  // G6 C1: the who-is-this question, when a door claimed a sender nobody has proven.
  const [who, setWho] = useState<WhoIsThisApi | null>(null);
  const [products, setProducts] = useState<ProductApi[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [instructions, setInstructions] = useState('');
  const [mode, setMode] = useState<ComposerMode>({ kind: 'typed' });
  // B17f — WHICH wire is down, not merely that one is. The composer posts to
  // one reply door and the server routes by channel, so this screen needs no
  // idea which it is until something is missing — and then it must say the
  // right thing. "Its mailbox is switched on" is a lie on a Telegram thread.
  const [sendDown, setSendDown] = useState<null | 'mail' | 'telegram'>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // A receipt, not a failure: "it left, the record lags" must never wear the
  // refusal slot's clothes.
  const [sendNote, setSendNote] = useState<string | null>(null);
  const pollCount = useRef(0);

  function refreshThread() {
    return getThread(tenantId, id)
      .then(({ thread, messages, whoIsThis }) => {
        setWho(whoIsThis ?? null);
        setLoad({ phase: 'ready', thread, messages });
      })
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the thread could not be read',
        });
      });
  }

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    setSummary({ phase: 'idle' });
    setAttribution(null);
    setMode({ kind: 'typed' });
    setComposerText('');
    pollCount.current = 0;

    void refreshThread();
    void getThreadAttribution(tenantId, id)
      .then((r) => { if (!cancelled) setAttribution(r.attribution); })
      .catch(() => { /* the chip simply doesn't render — the thread is whole without it */ });

    // Summary on open, polled while composing (B13b's contract).
    const poll = () => {
      if (cancelled) return;
      void getThreadSummary(tenantId, id)
        .then((r) => {
          if (cancelled) return;
          if (r.status === 'ready' && r.summary) setSummary({ phase: 'ready', summary: r.summary });
          else if (r.status === 'not_needed') setSummary({ phase: 'none' });
          else if (r.status === 'failed') setSummary({ phase: 'failed' });
          else {
            setSummary({ phase: 'composing' });
            pollCount.current += 1;
            if (pollCount.current < SUMMARY_POLL_MAX) setTimeout(poll, SUMMARY_POLL_MS);
            else setSummary({ phase: 'failed' });
          }
        })
        .catch(() => { if (!cancelled) setSummary({ phase: 'failed' }); });
    };
    poll();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  async function openPicker() {
    setPickerOpen(true);
    if (!products) {
      try { setProducts(await listProducts(tenantId)); } catch { setProducts([]); }
    }
  }

  async function correctAttribution(productId: string | null) {
    setPickerOpen(false);
    setActionError(null);
    try {
      const r = await putThreadAttribution(tenantId, id, productId);
      setAttribution(r.attribution);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'the correction was not recorded');
    }
  }

  function mailFailure(err: unknown): void {
    if (err instanceof ApiError && err.code === 'mail_not_configured') {
      setSendDown('mail');
      return;
    }
    if (err instanceof ApiError && err.code === 'telegram_not_configured') {
      setSendDown('telegram');
      return;
    }
    if (err instanceof ApiError && err.code === 'record_failed') {
      // It LEFT; only the thread row lags and the next sync heals it.
      // Never phrased as a failure that invites a retry.
      setSendNote('Sent — the thread record is catching up; it will appear shortly.');
      setComposerText('');
      setMode({ kind: 'typed' });
      return;
    }
    setActionError(err instanceof ApiError ? err.message : 'nothing was sent');
    setMode((m) => (m.kind === 'sending' ? { kind: 'typed' } : m));
  }

  async function sendTyped() {
    if (!composerText.trim()) return;
    setActionError(null);
    setSendNote(null);
    setMode({ kind: 'sending' });
    try {
      await sendThreadReply(tenantId, id, composerText.trim());
      setComposerText('');
      setMode({ kind: 'typed' });
      await refreshThread(); // the receipt: the row the server recorded
    } catch (err) {
      mailFailure(err);
      setMode((m) => (m.kind === 'sending' ? { kind: 'typed' } : m));
    }
  }

  async function draftWithAi() {
    setActionError(null);
    setMode({ kind: 'drafting' });
    try {
      const r = await draftThreadReply(tenantId, id, instructions.trim() || undefined);
      setComposerText(r.draft.body);
      setMode({ kind: 'draft', proposalId: r.proposalId, rationale: r.draft.rationale, subject: r.draft.subject });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'the draft could not be written');
      setMode({ kind: 'typed' });
    }
  }

  async function sendDraft() {
    if (mode.kind !== 'draft' || !composerText.trim()) return;
    const { proposalId } = mode;
    setActionError(null);
    setSendNote(null);
    setMode({ kind: 'sending' });
    try {
      await approveEmailDraft(tenantId, proposalId, { body: composerText.trim() });
      setComposerText('');
      setInstructions('');
      setMode({ kind: 'typed' });
      await refreshThread();
    } catch (err) {
      mailFailure(err);
      setMode((m) => (m.kind === 'sending' ? { kind: 'draft', proposalId, rationale: '', subject: '' } : m));
    }
  }

  async function markDone() {
    setActionError(null);
    try {
      await setThreadState(tenantId, id, 'closed');
      (onDone ?? onBack)();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'the thread was not closed');
    }
  }

  async function reopen() {
    setActionError(null);
    try {
      const r = await setThreadState(tenantId, id, 'open');
      setLoad((cur) => (cur.phase === 'ready' ? { ...cur, thread: r.thread } : cur));
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'the thread was not reopened');
    }
  }

  async function discardDraft() {
    if (mode.kind !== 'draft') return;
    const { proposalId } = mode;
    // Discarded model words are DISCARDED — the composer clears rather than
    // keeping them to be re-sent as if typed.
    setComposerText('');
    setMode({ kind: 'typed' });
    try { await rejectProposal(tenantId, proposalId, 'discarded from the thread'); } catch { /* already decided elsewhere is fine */ }
  }

  const productName = (pid: string | null) =>
    pid === null ? 'none' : products?.find((p) => p.id === pid)?.name ?? null;

  return (
    <div class="tspread">
      <BackLink label={backLabel ?? 'Threads'} onClick={onBack} />

      {load.phase === 'loading' ? (
        <div class="tspread__skeleton" aria-hidden="true">
          <div class="tspread__skeleton-block" />
          <div class="tspread__skeleton-block" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="tspread__say">
          This thread could not be read.
          <br />
          <span class="tspread__say-soft">{load.message}</span>
        </div>
      ) : (
        <>
          <header class="tspread__head">
            {/* Meta row FIRST, subject below at full width — a control's
                position must never be a function of title length (Huan's
                2026-08-25 screenshot: a three-line subject pushed the state
                and Mark done off-screen). */}
            <div class="tspread__head-meta">
              <span class="tspread__state">{threadStateLabel(load.thread.state)}</span>
              {/* Closing and reopening are both EXPLICIT human acts — the
                  coded rule means no message ever silently reopens, so the
                  way back lives here too. */}
              {load.thread.state === 'closed' ? (
                <button type="button" class="tspread__done-btn" onClick={() => void reopen()}>
                  Reopen
                </button>
              ) : (
                <button type="button" class="tspread__done-btn" onClick={() => void markDone()}>
                  Mark done
                </button>
              )}
            </div>
            <h1 class="tspread__subject">{load.thread.subject ?? '(no subject)'}</h1>
          </header>

          {who ? <WhoIsThisPanel tenantId={tenantId} threadId={id} who={who} onAnswered={setWho} /> : null}

          {/* Attribution: the chip is the current answer; tapping it is the
              one-action correction. Absent chip = unfiled, which the chip
              itself says. */}
          <div class="tspread__attribution">
            <button type="button" class="tspread__attr-chip" onClick={() => (pickerOpen ? setPickerOpen(false) : void openPicker())}>
              {attribution
                ? <>
                    <span class="tspread__attr-product">{productName(attribution.productId) ?? 'filed'}</span>
                    <span class="tspread__attr-source">
                      {attribution.source}
                      {attribution.source === 'ai' && attribution.confidence !== null
                        ? ` ${Math.round(attribution.confidence * 100)}%` : ''}
                    </span>
                  </>
                : <span class="tspread__attr-product">unfiled</span>}
            </button>
            {pickerOpen ? (
              <div class="tspread__attr-picker">
                {(products ?? []).filter((p) => p.status === 'active').map((p) => (
                  <button type="button" key={p.id} class="tspread__attr-option" onClick={() => void correctAttribution(p.id)}>
                    {p.name}
                  </button>
                ))}
                <button type="button" class="tspread__attr-option" onClick={() => void correctAttribution(null)}>
                  none of these
                </button>
              </div>
            ) : null}
          </div>

          {/* The summary. 'none' renders nothing at all. */}
          {summary.phase === 'composing' ? (
            <div class="tspread__summary tspread__summary--composing" aria-hidden="true">
              <div class="tspread__summary-shimmer" />
              <div class="tspread__summary-shimmer tspread__summary-shimmer--short" />
            </div>
          ) : summary.phase === 'ready' ? (
            <div class="tspread__summary">
              <InterpretationLabel kind="interpreted">{summary.summary.text}</InterpretationLabel>
              <div class="tspread__summary-cite">
                <SourceCitationChip
                  source={`summary · ${summary.summary.model}`}
                  when={`${summary.summary.messageIds.length} messages read${summary.summary.truncated ? ', earliest omitted' : ''}`}
                />
              </div>
            </div>
          ) : summary.phase === 'failed' ? (
            <div class="tspread__summary-failed">Summary unavailable — the thread below is complete.</div>
          ) : null}

          <div class="tspread__messages">
            {load.messages.map((m) => (
              <article class={`tspread__msg tspread__msg--${m.direction}`} key={m.id}>
                <div class="tspread__msg-head">
                  <span class="tspread__msg-who">
                    {m.direction === 'inbound' ? (m.senderIdentity ?? 'them') : (m.senderIdentity ?? 'you')}
                  </span>
                  <span class="tspread__msg-time">{messageTime(m.occurredAt)}</span>
                </div>
                <div class="tspread__msg-body">{m.body}</div>
              </article>
            ))}
          </div>

          {/* The composer. */}
          {sendDown ? (
            <div class="tspread__mail-down">
              {sendDown === 'mail'
                ? 'This instance cannot send yet — replies open the moment its mailbox is switched on.'
                : 'This instance has no Telegram bot — replies open the moment one is connected.'}
            </div>
          ) : (
            <div class="tspread__composer">
              {mode.kind === 'draft' ? (
                <div class="tspread__draft-banner">
                  <span class="tspread__draft-word">drafted</span>
                  <span class="tspread__draft-rationale">{mode.rationale}</span>
                </div>
              ) : null}
              <textarea
                class="tspread__composer-text"
                rows={mode.kind === 'draft' ? 8 : 3}
                placeholder="Reply…"
                value={composerText}
                disabled={mode.kind === 'drafting' || mode.kind === 'sending'}
                onInput={(e) => setComposerText((e.target as HTMLTextAreaElement).value)}
              />
              {mode.kind === 'typed' || mode.kind === 'drafting' ? (
                <input
                  class="tspread__composer-instructions"
                  type="text"
                  placeholder="or tell the draft what to say…"
                  value={instructions}
                  disabled={mode.kind === 'drafting'}
                  onInput={(e) => setInstructions((e.target as HTMLInputElement).value)}
                />
              ) : null}
              <div class="tspread__composer-actions">
                {mode.kind === 'draft' ? (
                  <>
                    <button type="button" class="tspread__btn tspread__btn--primary" onClick={() => void sendDraft()}>
                      Send draft
                    </button>
                    <button type="button" class="tspread__btn" onClick={() => void discardDraft()}>
                      Discard
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      class="tspread__btn tspread__btn--primary"
                      disabled={mode.kind !== 'typed' || !composerText.trim()}
                      onClick={() => void sendTyped()}
                    >
                      Send
                    </button>
                    <button
                      type="button"
                      class="tspread__btn"
                      disabled={mode.kind !== 'typed'}
                      onClick={() => void draftWithAi()}
                    >
                      {mode.kind === 'drafting' ? 'Drafting…' : 'Draft with AI'}
                    </button>
                  </>
                )}
              </div>
              {sendNote ? (
                <div class="tspread__action-note tspread__action-note--receipt" role="status">
                  {sendNote}
                </div>
              ) : null}
              {actionError ? (
                <div class="tspread__action-note" role="alert">
                  {actionError}
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}
